import { gql } from '@apollo/client';
import { graphql } from '../gql';

export const getDocumentById = graphql(/* GraphQL */ `
  query GetDocumentById($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      status
      shouldReturnForReceiverReview
      currentUserRole
      title
      currentTeamPossession
      eversignStatus
      document
      documentSuggestions {
        id
        externalId
        status
        # Author's role (creator|receiver|lost) — server-derived from the
        # suggestion author's teams vs the document's team/receiver_team. The
        # DOCX-native editor's accept/reject gate (decideSuggestion) needs the
        # AUTHOR's role, and this is the authoritative source.
        authorRole
      }
      deal {
        id
        createDate
        dealType
        dealSizeInSquareFeet
        createDate
        receiverEmail
        receiverInvitedDate
        receiverAcceptedInvitationDate
      }
      team {
        id
        adminUser {
          id
          email
          fullName
        }
      }
      receiverTeam {
        id
        adminUser {
          id
          email
          fullName
        }
      }
      receiverTeamAssignedAt
      eversignPdfUrl
      signedPdfUrl
      nativeAuditTrailUrl
      sharedBy
      signers {
        id
        email
        signedAt
        embeddedSigningUrl
        order
      }
      importedAssetUrl
      source
      importedAssetDownloadPath
      importedAssetContentType
      generatedDocumentPdfUrl
      approvalRequest {
        id
        status
        threshold
        permitEditingDuringApproval
        useApprovalOrder
        currentApprover {
          id
          fullName
          email
        }
        creator {
          id
        }
        reviewers {
          id
        }
        reviews {
          id
          approvedAt
          rejectedAt
          approvalOrder
          lastReminderSentAt
          reviewer {
            id
            fullName
            email
          }
        }
        totalReviews
        totalApproved
      }

      isEditable
      isTitleEditable
      editorConfiguration
      useSignerOrder
      extractedDocumentTerm {
        id
        documentType
        classification
        linkStatus
        anchorDocument {
          id
          title
        }
        termsData
        termsExtractedAt
        userEditedFields
        updatedAt
      }
      receiverCollaborationEnabled
      receiverPagePdfEditingEnabled
      updatedAt
      counterpartyPermissionLevel
      fieldPlacements {
        id
        type
        page
        x
        y
        w
        h
        signer
        req
        data
      }
      pdfFormFields
      pdfAnnotations
    }
  }
`);

export const getDocumentsByDeal = graphql(/* GraphQL */ `
  query GetDocumentsByDeal($dealId: ID!) {
    getDocumentsByDeal(dealId: $dealId) {
      id
      receiverTeam {
        id
        adminUser {
          id
        }
      }

      approvalRequest {
        id
        status
        threshold
        permitEditingDuringApproval
        useApprovalOrder
        currentApprover {
          id
          fullName
          email
        }
        creator {
          id
        }
        reviewers {
          id
        }
        reviews {
          id
          approvedAt
          rejectedAt
          approvalOrder
          lastReminderSentAt
          reviewer {
            id
            fullName
            email
          }
        }
        totalReviews
        totalApproved
      }
      possessionTeam {
        id
      }
      documentSuggestions {
        id
        externalId
        status
      }
      possessionTeam {
        id
      }
      isEditable
      isTitleEditable
      currentTeamPossession
      lastUpdated
      title
      status
      currentUserRole
      eversignPdfUrl
      signedPdfUrl
      nativeAuditTrailUrl
      sharedBy
      receiverTeamAssignedAt
      shouldReturnForReceiverReview
      signers {
        id
        email
        signedAt
        embeddedSigningUrl
        order
      }
      useSignerOrder
      editorConfiguration
      importedAssetUrl
      source
      importedAssetDownloadPath
      importedAssetContentType
      generatedDocumentPdfUrl
      importedAssetContentType
      isEligibleForSigningPrep
      extractedDocumentTerm {
        id
        documentType
        classification
        linkStatus
        anchorDocument {
          id
          title
        }
        termsData
        termsExtractedAt
        userEditedFields
        updatedAt
      }
      receiverCollaborationEnabled
      receiverPagePdfEditingEnabled
    }
  }
`);

export const getDocumentTemplates = graphql(/* GraphQL */ `
  query getDocumentTemplates($teamId: ID, $templateType: String, $includeContent: Boolean = false) {
    getDocumentTemplates(teamId: $teamId, templateType: $templateType) {
      id
      country
      state
      name
      description
      dealType
      status
      hidden
      updatedAt
      customTemplate
      template @include(if: $includeContent)
      templateType
      editorConfiguration
      importedAssetContentType
      importedAssetUrl
      source
      sharedToAffiliatedWorkspaces
      sharedFromTeam {
        id
        name
      }
      team {
        id
        name
      }
      folder {
        id
        name
        path
      }
      # Draft versioning fields
      draftTemplate @include(if: $includeContent)
      hasPendingChanges
      draftUpdatedAt
      workingTemplate @include(if: $includeContent)
      publishedTemplate @include(if: $includeContent)
      hasPendingSuggestions
      canPublish
    }
  }
`);

export const getWorkspaceSharedTemplatesByUser = graphql(/* GraphQL */ `
  query workspaceSharedTemplatesByUser($teamId: ID, $templateType: String, $includeContent: Boolean = false) {
    workspaceSharedTemplatesByUser(teamId: $teamId, templateType: $templateType) {
      id
      country
      state
      name
      description
      dealType
      status
      hidden
      updatedAt
      customTemplate
      template @include(if: $includeContent)
      templateType
      editorConfiguration
      importedAssetContentType
      importedAssetUrl
      source
      sharedToAffiliatedWorkspaces
      sharedFromTeam {
        id
        name
      }
      team {
        id
        name
      }
      folder {
        id
        name
        path
      }
      # Draft versioning fields
      draftTemplate @include(if: $includeContent)
      hasPendingChanges
      draftUpdatedAt
      workingTemplate @include(if: $includeContent)
      publishedTemplate @include(if: $includeContent)
      hasPendingSuggestions
      canPublish
    }
  }
`);

export const getOrganizationsTemplatesByUser = gql`
  query organizationsTemplatesByUser($teamId: ID, $templateType: String, $includeContent: Boolean = false) {
    organizationsTemplatesByUser(teamId: $teamId, templateType: $templateType) {
      id
      country
      state
      name
      description
      dealType
      status
      hidden
      updatedAt
      customTemplate
      template @include(if: $includeContent)
      templateType
      editorConfiguration
      importedAssetContentType
      importedAssetUrl
      source
      name
      team {
        name
        id
        uuid
        organization {
          id
          name
        }
      }
      folder {
        id
        name
        path
      }
      # Draft versioning fields
      draftTemplate @include(if: $includeContent)
      hasPendingChanges
      draftUpdatedAt
      workingTemplate @include(if: $includeContent)
      publishedTemplate @include(if: $includeContent)
      hasPendingSuggestions
      canPublish
    }
  }
`;

export const getDocumentTemplateById = gql`
  query getDocumentTemplateById($documentTemplateId: ID!, $teamId: ID) {
    getDocumentTemplateById(documentTemplateId: $documentTemplateId, teamId: $teamId) {
      id
      country
      state
      name
      description
      dealType
      customTemplate
      updatedAt
      template
      source
      importedAssetUrl
      importedAssetContentType
      sharedToAffiliatedWorkspaces
      hidden
      status
      sharedFromTeam {
        id
        name
      }
      team {
        id
        name
        uuid
        organization {
          id
          name
        }
      }
      folder {
        id
        name
        path
      }
      editorConfiguration
      templateType
      # Draft versioning fields
      draftTemplate
      hasPendingChanges
      draftUpdatedAt
      workingTemplate
      publishedTemplate
      hasPendingSuggestions
      canPublish
    }
  }
`;

export const getDealTeamsByTeamId = gql`
  query GetDealTeamsByDealId($dealId: ID!) {
    getDealById(dealId: $dealId) {
      id
      receiverAcceptedInvitationDate
      receiverEmail
      dealAccessUsers
      receiverTeam {
        id
        adminUser {
          id
          email
          fullName
        }
        approvers(dealId: $dealId) {
          id
          fullName
          companyName
          email
        }
        teamMembers {
          user {
            id
            email
            fullName
          }
          teamPermissions {
            key
            allowed
          }
        }
      }
      team {
        id
        approvers(dealId: $dealId) {
          id
          fullName
          companyName
          email
        }
        teamMembers {
          user {
            id
            email
            fullName
          }
          teamPermissions {
            key
            allowed
          }
        }
      }
      members {
        id
        email
        fullName
      }
      sameSideMembers {
        id
        email
        fullName
      }
      timelineMembers {
        id
        email
        fullName
      }
    }
  }
`;

export const getDealCreatorTeamsByDealId = gql`
  query GetDealCreatorTeamsByDealId($dealId: ID!) {
    getDealById(dealId: $dealId) {
      id
      team {
        teamMembers {
          user {
            id
            email
            fullName
          }
          teamPermissions {
            key
            allowed
          }
        }
      }
      members {
        id
      }
    }
  }
`;

export const getCurrentUserTeamsWhereUserHasEditPermissions = gql`
  query GetCurrentUserTeamsWhereUserHasEditPermissions {
    currentUser {
      id
      teamPermissions {
        key
        allowed
        teamId
        teamName
      }
      ownedTeams {
        id
        name
      }
    }
  }
`;

export const getPermissions = graphql(/* GraphQL */ `
  query GetPermissions {
    permissions {
      id
      name
      key
      displayOrder
      description
    }
  }
`);

export const getDocumentSigners = graphql(/* GraphQL */ `
  query GetDocumentSigners($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      useSignerOrder
      signers {
        id
        email
        order
        signedAt
      }
    }
  }
`);

export const getDocumentRecipients = graphql(/* GraphQL */ `
  query GetDocumentRecipients($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      ccRecipients
    }
  }
`);

/** Version-history metadata for a deal doc's Compare panel. Fetches everything
 *  the compare list needs — name, timestamp, author, whether a body exists —
 *  but NOT the bodies themselves (that would download every snapshot up front).
 *  The body is loaded on demand via `getDocumentRevisionBody`. */
/** Version-history metadata for a TEMPLATE's Compare panel — mirrors the deal
 *  query but queries the template root. Revisions are polymorphic
 *  (Cke::DocumentRevisionType), so the fields match exactly. */
export const getDocumentTemplateRevisionsForCompare = graphql(/* GraphQL */ `
  query GetDocumentTemplateRevisionsForCompare($documentTemplateId: ID!, $teamId: ID) {
    getDocumentTemplateById(documentTemplateId: $documentTemplateId, teamId: $teamId) {
      id
      documentRevisions {
        id
        revisionId
        name
        event
        hasSnapshot
        snapshotFormat
        createdAt
        creator {
          id
          fullName
          email
        }
      }
    }
  }
`);

/** On-demand body for ONE template revision (snapshotContent + snapshotFormat).
 *  Split from the metadata query so the Compare list stays light. */
export const getDocumentTemplateRevisionBody = graphql(/* GraphQL */ `
  query GetDocumentTemplateRevisionBody($documentTemplateId: ID!, $teamId: ID) {
    getDocumentTemplateById(documentTemplateId: $documentTemplateId, teamId: $teamId) {
      id
      documentRevisions {
        id
        revisionId
        snapshotContent
        snapshotFormat
      }
    }
  }
`);

export const getDocumentRevisionsForCompare = graphql(/* GraphQL */ `
  query GetDocumentRevisionsForCompare($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      documentRevisions {
        id
        revisionId
        name
        event
        hasSnapshot
        snapshotFormat
        createdAt
        creator {
          id
          fullName
          email
        }
      }
    }
  }
`);

/** On-demand body for ONE revision — `data.content` carries the snapshot body
 *  and `data.content_format` its format. Split from the metadata query so the
 *  Compare list stays light and bodies load only when a version is picked. */
export const getDocumentRevisionBody = graphql(/* GraphQL */ `
  query GetDocumentRevisionBody($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      documentRevisions {
        id
        revisionId
        snapshotContent
        snapshotFormat
      }
    }
  }
`);

export const getDocumentExtraAttachments = graphql(/* GraphQL */ `
  query GetDocumentExtraAttachments($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      extraAttachments {
        id
        attachment
        order
        linkedDocument {
          id
          title
          status
        }
        user {
          id
          email
        }
        createdAt
        updatedAt
      }
    }
  }
`);

export const getDealDocumentsForPicker = graphql(/* GraphQL */ `
  query GetDealDocumentsForPicker($dealId: ID!) {
    getDealById(dealId: $dealId) {
      id
      documents {
        id
        title
        status
      }
    }
  }
`);

export const getGeneratedDocumentPdf = graphql(/* GraphQL */ `
  query GetDocumentPdfUrl($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      title
      generatedDocumentPdfUrl
    }
  }
`);

export const getGeneratedTemplatePdf = graphql(/* GraphQL */ `
  query GetTemplatePdfUrl($documentTemplateId: ID!, $teamId: ID) {
    getDocumentTemplateById(documentTemplateId: $documentTemplateId, teamId: $teamId) {
      id
      name
      generatedDocumentPdfUrl
    }
  }
`);

export const getDocumentFeedbackById = graphql(/* GraphQL */ `
  query GetDocumentFeedback($id: ID!) {
    getDocumentFeedbackById(id: $id) {
      id
      document {
        id
      }
      subject
      sender
      htmlContent
      emailAttachments
      createdAt
    }
  }
`);

export const getAlertableDocumentsByTeam = graphql(/* GraphQL */ `
  query GetAlertableDocumentsByTeam($teamId: ID!, $dealId: ID) {
    getAlertableDocumentsByTeam(teamId: $teamId, dealId: $dealId) {
      id
    }
  }
`);

export const getDocumentPlaceholderCompleteness = graphql(/* GraphQL */ `
  query GetDocumentPlaceholderCompleteness($documentId: ID!, $documentType: String!) {
    getDocumentPlaceholderCompleteness(documentId: $documentId, documentType: $documentType) {
      placeholders {
        uuid
        id
        title
        isRequired
        status
      }
      success
    }
  }
`);

// Template Folders
export const getTemplateFolders = graphql(/* GraphQL */ `
  query getTemplateFolders($teamId: ID!, $folderType: String) {
    getTemplateFolders(teamId: $teamId, folderType: $folderType) {
      id
      name
      folderType
      path
      totalItemsCount
      canBeDeleted
      createdAt
      updatedAt
      parentFolder {
        id
        name
      }
      childFolders {
        id
        name
        totalItemsCount
      }
      documentTemplates {
        id
        name
        templateType
        status
        updatedAt
      }
    }
  }
`);

export const getTemplateFolderById = graphql(/* GraphQL */ `
  query getTemplateFolderById($id: ID!) {
    getTemplateFolderById(id: $id) {
      id
      name
      path
      totalItemsCount
      canBeDeleted
      createdAt
      updatedAt
      team {
        id
        name
      }
      parentFolder {
        id
        name
        path
      }
      childFolders {
        id
        name
        totalItemsCount
        canBeDeleted
      }
      documentTemplates {
        id
        name
        templateType
        status
        updatedAt
        description
      }
    }
  }
`);

// Deal Folders
export const getDealFolders = graphql(/* GraphQL */ `
  query getDealFolders($teamId: ID!) {
    dealFolders(teamId: $teamId) {
      folders {
        id
        name
        parentFolder {
          id
          name
        }
        childFolders {
          id
          name
        }
        path
        canModify
        canDelete
        createdAt
      }
      showSharedFolder
    }
  }
`);

export const cloneTargetTeams = graphql(/* GraphQL */ `
  query CloneTargetTeams($sourceTeamId: ID!) {
    cloneTargetTeams(sourceTeamId: $sourceTeamId) {
      id
      name
    }
  }
`);

export const searchDocumentsByTeam = graphql(/* GraphQL */ `
  query SearchDocumentsByTeam($teamId: ID!, $searchQuery: String) {
    searchDocumentsByTeam(teamId: $teamId, searchQuery: $searchQuery) {
      id
      title
      status
      deal {
        id
        title
      }
    }
  }
`);

export const GET_EXTRACTED_DATA_BY_TEAM = gql`
  query GetExtractedDataByTeam(
    $teamId: ID!
    $documentType: String
    $filters: [ExtractedTermsFilterInput!]
  ) {
    extractedDataByTeam(
      teamId: $teamId
      documentType: $documentType
      filters: $filters
    ) {
      documents {
        id
        title
        status
        deal {
          id
          title
        }
        extractedDocumentTerm {
          id
          documentType
          classification
          linkStatus
          effectiveOn
          termsData
          termsExtractedAt
          updatedAt
          isAmendment
          isPendingLink
          hasAmendments
          anchorDocument {
            id
            title
          }
          linkedAmendments {
            id
            classification
            effectiveOn
            termsData
            documentType
            document {
              id
              title
            }
          }
        }
      }
    }
  }
`;

export const getDealActivities = graphql(/* GraphQL */ `
  query GetDealActivities($dealId: ID!, $limit: Int, $beforeId: ID) {
    getDealById(dealId: $dealId) {
      id
      dealActivities(limit: $limit, beforeId: $beforeId) {
        id
        verb
        time
        description
        actorData
        objectData
        additionalMessage
      }
    }
  }
`);

export const getDocumentActivities = graphql(/* GraphQL */ `
  query GetDocumentActivities($documentId: ID!, $limit: Int, $beforeId: ID) {
    getDocumentById(id: $documentId) {
      id
      documentActivities(limit: $limit, beforeId: $beforeId) {
        id
        verb
        time
        description
        actorData
        objectData
        extra
        inactive
      }
    }
  }
`);
